import { useMemo } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { SelectField } from '@brighte/form-components';
import { EligibilityType, eligibilitySchema } from '@brighte/validation';
import { StepNavigator } from '@components/StepNavigator';
import { useApplicationData } from '@provider';
import { Box } from '@spark-web/box';
import { Heading } from '@spark-web/heading';
import { Row } from '@spark-web/row';
import { Stack } from '@spark-web/stack';
import { Text } from '@spark-web/text';
import { APPLICATION_STEPS, ELIGIBILITY, PLACEHOLDER, getFormFields, isFieldExists, productIds } from '@utils';
import Terms, { CustomerDeclaration } from './Terms';
import { getRequirements } from './helpers';

function Eligibility() {
  const {
    TITLE,
    DESCRIPTION,
    UNSOLICITED_SALES,
    UNSOLICITED_SALES_OPTIONS,
    SFA_DESCRIPTION,
    HEADING_WHAT_YOU_NEED,
    WHAT_YOU_NEED,
  } = ELIGIBILITY;

  const { quote, Eligibility: eligibility } = useApplicationData(['quote', 'Eligibility']);
  const { financialProduct, vendor } = quote ?? {};
  const financialProductId = financialProduct?.id ?? '';
  const isMud = financialProductId === productIds.ACT_SFA;
  const whatYouNeed = useMemo(() => getRequirements(financialProductId), [financialProductId]);
  const additionalFields = useMemo(() => getFormFields(eligibilitySchema, financialProductId), [financialProductId]);

  return (
    <StepNavigator<EligibilityType>
      schema={eligibilitySchema}
      name={APPLICATION_STEPS.ELIGIBILITY}
      title={TITLE}
      defaultValues={eligibility ?? {}}
      isLoading={false}
    >
      {({ control, register }: UseFormReturn<EligibilityType>) => (
        <>
          <Text>{isMud ? SFA_DESCRIPTION : DESCRIPTION}</Text>
          <Terms control={control} financialProductId={financialProductId} />
          <CustomerDeclaration control={control} financialProductId={financialProductId} />
          <Box background="secondaryLow" padding="xlarge">
            <Stack gap="xlarge">
              <Heading level="3">{HEADING_WHAT_YOU_NEED}</Heading>
              {whatYouNeed.map(name => (
                <Row gap="medium" key={name} alignY="center">
                  <Box>{WHAT_YOU_NEED[name].icon}</Box>
                  <Text>{WHAT_YOU_NEED[name].text}</Text>
                </Row>
              ))}
            </Stack>
          </Box>
          {isFieldExists(additionalFields, 'applicant.unsolicitedSaleOptions') && (
            <SelectField
              label={`${UNSOLICITED_SALES} ${vendor?.tradingName}`}
              name="applicant.unsolicitedSaleOptions"
              control={control}
              placeholder={PLACEHOLDER}
              options={UNSOLICITED_SALES_OPTIONS}
            />
          )}
          <input type="hidden" {...register('financialProductId')} value={financialProductId} />
        </>
      )}
    </StepNavigator>
  );
}

export default Eligibility;
